import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { combineLatest, merge, Subscription, switchMap, tap } from 'rxjs';

import { UserLocationRole, UserRole } from '@shared/user/enums';
import { NavigationService } from '@tablesready/common-ui';
import {
  AuthenticatedInitializer,
  PermissionCheckerService,
} from '../../services';
import { ProfileInvitesService } from '../../settings/account/profile/services';
import { AuthUser } from '@shared/signup/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import {
  desktopMenuBreakpoint,
  desktopSettingsMenuBreakpoint,
  FrontendBreakpointsService,
} from '@tablesready/frontend-breakpoints';
import { AppIcon, AppIconSize } from '@tablesready/frontend-icons';
import {
  AuthRestaurantService,
  SocketService,
} from '@tablesready/frontend-store';
import { StatusService } from '@tablesready/frontend-status-bar';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { AuthService } from '@tablesready/frontend-auth';
import { APP_VARIABLES_TOKEN, AppVariables } from '@tablesready/frontend-variables';
import { NAVIGATION_ITEMS, NavigationItem } from '@tablesready/frontend-nav-sidebar';
import { IonContent, IonMenu } from '@ionic/angular';

@Component({
  selector: 'tablesready-authenticated-layout',
  styleUrls: ['./authenticated-layout.component.scss'],
  templateUrl: './authenticated-layout.component.html',
  providers: [AuthenticatedInitializer, PermissionCheckerService],
})
export class AuthenticatedLayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(IonContent) content?: IonContent;
  @ViewChild('sidebarRef') sidebarRef?: ElementRef;
  @ViewChild('belowHeaderSection') belowHeaderSection?: ElementRef;
  navItems: NavigationItem[] = [];
  observer: ResizeObserver;
  path: string;

  showDesktopMenu = false;
  isSettingsPage = false;
  sideMenuWidth = '6.25rem';
  settingsPaneOpen = false;
  defaultPage = '/';
  iconSize = AppIconSize;
  icon = AppIcon;
  user: AuthUser;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly authService: AuthService,
    private readonly statusService: StatusService,
    private readonly socketService: SocketService,
    private readonly alertsService: AlertsService,
    private readonly navService: NavigationService,
    private readonly router: Router,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly authenticatedInitializer: AuthenticatedInitializer,
    private readonly inviteDataService: ProfileInvitesService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly breakpointService: FrontendBreakpointsService,
    @Inject(APP_VARIABLES_TOKEN) private readonly appVariables: AppVariables,
  ) {
    this.path = this.appVariables.WHITELABEL_BRAND.toLowerCase();
  }

  ngAfterViewInit(): void {
    this.observer = new ResizeObserver((_) => {
      this.breakpointService.setMainNavSidebarWidth(this.sidebarRef.nativeElement.offsetWidth);
    });

    this.observer.observe(this.sidebarRef.nativeElement);
  }

  scrollStart(): void {
    console.log('AuthenticatedLayoutComponent ionScroll');
  }

  ngOnInit(): void {
    this.subscriptions.add(combineLatest([
      this.authRestaurantService.select(),
      this.inviteDataService.get(),
      this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.HandsetLandscape]),
    ])
      .pipe(tap(([user, accountsAccess, { matches: isMobileScreen }]) => {
        this.user = user;
        const navigationItems = [...NAVIGATION_ITEMS].filter((navItem) => {
          if (navItem.href === '/togo') {
            return !!(user.location?.displayOptions.togo && user.account?.features?.togo);
          }

          return true;
        });

        const hiddenLinks: Array<NavigationItem['href']> = [];
        this.statusService.setAccess(accountsAccess);

        if (user.role !== UserRole.admin) {
          hiddenLinks.push('/admin');
        }

        if (user.location) {
          if (user.locationUser.role === UserLocationRole.user) {
            hiddenLinks.push('/analytics');
          }

          hiddenLinks.push('/settings/account/profile');
        } else {
          hiddenLinks.push(
            '/waitlist',
            '/reservations',
            '/floorplan',
            '/history',
            '/analytics',
            '/customers',
            '/settings/preferences/notifications',
          );
          navigationItems.push({
            href: '/settings/account/profile',
            name: 'Settings',
            icon: AppIcon.settings,
          });
        }

        this.navItems = navigationItems
          .filter(nav => !hiddenLinks.includes(nav.href))
          .map((item) => {
            const labels = user.location?.displayOptions?.labels;
            if (item.href === '/reservations') {
              return this.setMenuItemTittle(item, labels?.reservation_plural);
            }
            if (item.href === '/customers') {
              return this.setMenuItemTittle(item, labels?.customer_plural);
            }

            if (item.href === '/floorplan' && (isMobileScreen || !user.account?.features?.floorPlan)) {
              return this.setMenuItemTittle(item, 'Timeline', AppIcon.grid);
            }

            return item;
          });

        this.updateStatusBar(user);

        if (this.navService.currentUrl.includes('/togo') && !(user.location?.displayOptions?.togo && user.account?.features?.togo)) {
          void this.router.navigateByUrl('/waitlist');
        }
      }))
      .subscribe());

    this.subscriptions.add(this.authService.refreshTokens().subscribe());

    let alertId: string | undefined = undefined;
    const connectionErrorSub = this.socketService.onConnectionError.subscribe(() => {
      const errorTemplate = 'It looks like there\'s a problem with your connection';

      alertId = this.alertsService.add({
        id: 'network-connection-lost',
        duration: 7 * 24 * 60 * 60 * 1000,
        time: Date.now(),
        type: IAlertTypes.danger,
        heading: 'Oops.',
        body: errorTemplate,
        actions: [
          {
            label: 'Click here to reload',
            action: (): void => {
              this.reload();
            },
          },
        ],
      });
    });
    this.subscriptions.add(connectionErrorSub);

    const websocketAuthenticatedSubscription = this.socketService.onAuthenticated
      .pipe(tap(() => {
        if (alertId) {
          this.alertsService.clear();
        }
      }))
      .subscribe();
    this.subscriptions.add(websocketAuthenticatedSubscription);

    const locationChangeSub = this.authRestaurantService.selectAndDetectLocationChanges()
      .pipe(tap((data) => {
        this.updateStatusBar(data);
        this.authService.updateDataLayer(data);
      }))
      .subscribe();
    this.subscriptions.add(locationChangeSub);

    const invitesSub = this.socketService.getEventStream(WebSocketEventsEnum.invite)
      .pipe(switchMap(() => this.inviteDataService.get())).subscribe();
    this.subscriptions.add(invitesSub);

    const desktopSub = merge(
      this.breakpointService.observeDesktopMode(),
      this.breakpointService.observeSettingsDesktopMode(),
      this.navService.navEvents$,
    )
      .subscribe(() => {
        this.isSettingsPage = this.navService.currentUrl.includes('/settings');
        this.showDesktopMenu = this.isSettingsPage
          ? this.breakpointService.isSettingsDesktopMode : this.breakpointService.isDesktopMode;
      });
    this.subscriptions.add(desktopSub);
  }

  sidebarItemClick(nav: NavigationItem, menu: IonMenu): void {
    this.settingsPaneOpen = nav.name === 'Settings';
    if (this.settingsPaneOpen) {
      return;
    }

    void menu.close(true);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.observer.unobserve(this.sidebarRef.nativeElement);
  }

  onLogout(): void {
    this.authService.logout();
  }

  reload(): void {
    window.location.reload();
  }

  private setMenuItemTittle(
    item: NavigationItem,
    name: string,
    icon?: AppIcon,
  ) {
    item = { ...item };
    (item as any).name = name?.replace(/\b\w/, m => m.toUpperCase());

    if (icon) {
      item.icon = icon;
    }

    return item;
  }

  private updateStatusBar(user: AuthUser): void {
    this.statusService.updateStatusBarAlerts(user);
  }

  protected readonly desktopSettingsMenuBreakpoint = desktopSettingsMenuBreakpoint;
  protected readonly desktopMenuBreakpoint = desktopMenuBreakpoint;
  protected readonly location = location;
}
